import { CherryPayApi } from "./models";
import axios, { Axios, AxiosResponse } from "axios";
import { User } from "firebase/auth";
import DateUtil from "../util/DateUtil";

export type ApiError = { statusCode?: number; message: string; ok: false };
export type ApiResult<T> =
  | { statusCode: number; data: T; ok: true; continuationToken?: string }
  | ApiError;

export const apiResult = <T>(
  axiosResponse: AxiosResponse,
  getData?: (data: any) => T
): ApiResult<T> => {
  if (axiosResponse.status >= 200 && axiosResponse.status <= 299) {
    // If the response body is paginated we'll find a url for the next page.
    // change approach to pass continuation token instead of relative url, there's an issue with encoding url with date/time
    // const next = axiosResponse.data?._links?.next?.relativeUrl ?? undefined;
    const continuationToken =
      axiosResponse.data?._links?.next?.continuationToken ?? undefined;

    return {
      data: (getData ? getData(axiosResponse.data) : axiosResponse.data) as T,
      statusCode: axiosResponse.status,
      ok: true,
      continuationToken,
    };
  } else if (axiosResponse.status) {
    return {
      statusCode: axiosResponse.status,
      message:
        axiosResponse.data?.ErrorMessage ??
        `HTTP ${axiosResponse.status} error.`,
      ok: false,
    };
  } else {
    return {
      statusCode: axiosResponse.status,
      message: `Unknown error.`,
      ok: false,
    };
  }
};

export class ApiClient {
  cherryPayClient: Axios;

  constructor(user: User) {
    this.cherryPayClient = axios.create({
      baseURL: `${API_BASE_URL}`,
      // HTTP errors will not throw exceptions
      validateStatus: () => true,
    });

    this.cherryPayClient.interceptors.request.use((config) => {
      // Fetches the current access token. If it has expired or is close to exiring a new token will be fetched.
      return user.getIdTokenResult().then((idToken) => {
        if (!config.headers) {
          config.headers = {};
        }
        config.headers!.Authorization = `Bearer ${idToken.token}`;
        return config;
      });
    });
  }

  async getCardTransactionSummary(
    businessId: string,
    cardId: string,
    limit: number,
    continuationToken?: string
  ): Promise<ApiResult<CherryPayApi.CherryPayCardTransactionResult>> {
    const result = await this.cherryPayClient.get(
      `/cherrypay/v1/${businessId}/cards/${cardId}/transactions`,
      {
        params: {
          limit,
          continuationToken: continuationToken,
        },
      }
    );
    return apiResult<CherryPayApi.CherryPayCardTransactionResult>(result);
  }

  async changeCardAccountStatus(
    businessId: string,
    cardId: string,
    status: string,
    externalAccountId?: string | null
  ): Promise<ApiResult<CherryPayApi.CherryPayCardAccountStatus>> {
    let url = `/cherrypay/v1/${businessId}/cards/${cardId}/set-account-status/${status}`;
    if (externalAccountId) {
      url = url + `?externalAccountId=${externalAccountId}`;
    }
    const result = await this.cherryPayClient.post(url);
    return apiResult<CherryPayApi.CherryPayCardAccountStatus>(result);
  }

  async getCardDetailById(
    businessId: string,
    cardId: string
  ): Promise<ApiResult<CherryPayApi.CherryPayCardCompact>> {
    const result = await this.cherryPayClient.get(
      `/cherrypay/v1/${businessId}/cards/${cardId}`
    );
    return apiResult<CherryPayApi.CherryPayCardCompact>(result);
  }

  async getCardAccountStatus(
    businessId: string,
    cardId: string
  ): Promise<ApiResult<CherryPayApi.CherryPayCardAccountStatus>> {
    const result = await this.cherryPayClient.get(
      `/cherrypay/v1/${businessId}/cards/${cardId}/account-status`
    );
    return apiResult<CherryPayApi.CherryPayCardAccountStatus>(result);
  }

  /**
   * @param businessId
   * @returns configurations for business, such as card program types
   */
  async getConfiguration(
    businessId: string
  ): Promise<ApiResult<CherryPayApi.BusinessConfiguration>> {
    const result = await this.cherryPayClient.get(
      `/cherrypay/v1/${businessId}/configuration`
    );
    return apiResult<CherryPayApi.BusinessConfiguration>(result);
  }

  async getNewCardOrder(
    businessId: string,
    memberId: string,
    cardProgramId: string,
    signal?: AbortSignal
  ): Promise<ApiResult<CherryPayApi.CardOrderResponse>> {
    const result = await this.cherryPayClient.get(
      `/cherrypay/v1/${businessId}/members/${memberId}/cards/orders/new-order/${cardProgramId}`,
      { signal }
    );
    return apiResult<CherryPayApi.CardOrderResponse>(result);
  }

  async putCardOrderMemberDetail(
    businessId: string,
    memberId: string,
    orderId: string,
    data: CherryPayApi.CardOrderMemberDetailsUpdate
  ): Promise<ApiResult<CherryPayApi.CardOrderResponse>> {
    const result = await this.cherryPayClient.put(
      `/cherrypay/v1/${businessId}/members/${memberId}/cards/orders/${orderId}/member-details`,
      data
    );
    return apiResult<CherryPayApi.CardOrderResponse>(result);
  }

  async confirmCardOrder(
    businessId: string,
    memberId: string,
    orderId: string
  ): Promise<ApiResult<CherryPayApi.CardOrderResponse>> {
    const result = await this.cherryPayClient.post(
      `/cherrypay/v1/${businessId}/members/${memberId}/cards/orders/${orderId}/confirm`
    );
    return apiResult<CherryPayApi.CardOrderResponse>(result);
  }

  async getCardMembersReport(
    businessId: string,
    limit: number,
    cardProgramId?: string,
    startDate?: string,
    endDate?: string,
    continuationToken?: string
  ): Promise<ApiResult<CherryPayApi.CherryPayCardsByMemberReportResult>> {
    const result = await this.cherryPayClient.get(
      `/cherrypay/v1/${businessId}/reports/cards/cherrypay-cards-by-member`,
      {
        params: {
          limit,
          startDate,
          endDate,
          cardProgramId,
          continuationToken,
        },
      }
    );
    return apiResult<CherryPayApi.CherryPayCardsByMemberReportResult>(result);
  }

  async getZeroBalanceInactiveMemberCardsReport(
    businessId: string,
    options: {
      limit: number;
      memberFilterQuery?: string;
      cardProgramId?: string;
      continuationToken?: string;
    }
  ): Promise<
    ApiResult<CherryPayApi.ZeroBalanceInactiveMemberCardReportResult>
  > {
    const params = new URLSearchParams();

    Object.entries(options).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        params.append(key, String(value));
      }
    });

    const result = await this.cherryPayClient.get(
      `/cherrypay/v1/${businessId}/reports/cards/cherrypay-cards-zero-balance-by-member`,
      { params }
    );
    return apiResult<CherryPayApi.ZeroBalanceInactiveMemberCardReportResult>(
      result
    );
  }

  async getZeroBalanceInactiveMemberCardsReportCsv(
    businessId: string,
    options: {
      memberFilterQuery?: string;
      cardProgramId?: string;
    }
  ) {
    const params = new URLSearchParams();

    Object.entries(options).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        params.append(key, String(value));
      }
    });

    const result = await this.cherryPayClient.get(
      `/cherrypay/v1/${businessId}/reports/cards/cherrypay-cards-zero-balance-by-member/csv`,
      { params }
    );

    return apiResult<any>(result);
  }

  async bulkCloseCards(
    businessId: string,
    request: CherryPayApi.BulkCloseCherryPayCardsRequest
  ): Promise<ApiResult<CherryPayApi.BulkCloseCherryPayCardsResponse>> {
    const result = await this.cherryPayClient.post(
      `/cherrypay/v1/${businessId}/cards/bulk-close`,
      request
    );
    return apiResult<CherryPayApi.BulkCloseCherryPayCardsResponse>(result);
  }

  async getCardSummary(
    businessId: string,
    cardProgramId?: string,
    createdFrom?: string,
    createdTo?: string,
    limit?: number,
    q?: string,
    continuationToken?: string
  ): Promise<ApiResult<CherryPayApi.CherryPayCardSummaryResult>> {
    const result = await this.cherryPayClient.get(
      `/cherrypay/v1/${businessId}/cards`,
      {
        params: {
          createdFromDate: createdFrom,
          createdToDate: createdTo,
          cardProgramId,
          q,
          limit,
          continuationToken,
        },
      }
    );
    return apiResult<CherryPayApi.CherryPayCardSummaryResult>(result);
  }

  async getCardOrderSummary(
    businessId: string,
    createdFrom?: string,
    createdTo?: string,
    limit?: number,
    q?: string,
    externalAccountId?: string,
    orderStatus?: string,
    continuationToken?: string
  ): Promise<ApiResult<CherryPayApi.CherryPayCardOrderSummaryResult>> {
    const result = await this.cherryPayClient.get(
      `/cherrypay/v1/${businessId}/card-orders`,
      {
        params: {
          createdFromDate: createdFrom,
          createdToDate: createdTo,
          q,
          externalAccountId,
          status: orderStatus,
          limit,
          continuationToken,
        },
      }
    );
    return apiResult<CherryPayApi.CherryPayCardOrderSummaryResult>(result);
  }

  async cancelCardOrder(
    businessId: string,
    cardOrderId: string
  ): Promise<ApiResult<CherryPayApi.CherryPayCardOrder>> {
    const result = await this.cherryPayClient.post(
      `/cherrypay/v1/${businessId}/card-orders/${cardOrderId}/cancel`,
      {}
    );

    return apiResult<CherryPayApi.CherryPayCardOrder>(result);
  }

  async archiveCardOrder(
    businessId: string,
    cardOrderId: string
  ): Promise<ApiResult<CherryPayApi.CherryPayCardOrder>> {
    const result = await this.cherryPayClient.post(
      `/cherrypay/v1/${businessId}/card-orders/${cardOrderId}/archive`,
      {}
    );

    return apiResult<CherryPayApi.CherryPayCardOrder>(result);
  }

  async getCardOrderDetail(
    businessId: string,
    memberId: string,
    orderId: string
  ): Promise<ApiResult<CherryPayApi.CardOrderResponse>> {
    const result = await this.cherryPayClient.get(
      `/cherrypay/v1/${businessId}/members/${memberId}/cards/orders/${orderId}`
    );
    return apiResult<CherryPayApi.CardOrderResponse>(result);
  }

  async getCardSummaryPage(
    url: string
  ): Promise<ApiResult<CherryPayApi.CherryPayCardSummaryResult>> {
    const result = await this.cherryPayClient.get(url);
    return apiResult<CherryPayApi.CherryPayCardSummaryResult>(result);
  }

  async getCardMembersReportPage(
    url: string
  ): Promise<ApiResult<CherryPayApi.CherryPayCardsByMemberReportResult>> {
    const result = await this.cherryPayClient.get(url);
    return apiResult<CherryPayApi.CherryPayCardsByMemberReportResult>(result);
  }

  async getCardByMembersReport(
    businessId: string,
    cardProgramId?: string,
    startDate?: string,
    endDate?: string
  ) {
    const result = await this.cherryPayClient.get(
      `/cherrypay/v1/${businessId}/reports/cards/cherrypay-cards-by-member/csv`,
      {
        params: {
          startDate,
          endDate,
          cardProgramId,
          timeZone: DateUtil.getBrowserTimeZone(),
        },
      }
    );

    return apiResult<any>(result);
  }

  async getDisbursementAccountStatus(
    businessId: string
  ): Promise<ApiResult<CherryPayApi.CherryPayDisbursementAccountStatus>> {
    const result = await this.cherryPayClient.get(
      `/cherrypay/v1/${businessId}/reports/cards/disbursement-account-status`
    );
    return apiResult<CherryPayApi.CherryPayDisbursementAccountStatus>(result);
  }

  async getCardsKpiAll(
    businessId: string
  ): Promise<ApiResult<CherryPayApi.CherryPayCardsKpiAll>> {
    const result = await this.cherryPayClient.get(
      `/cherrypay/v1/${businessId}/reports/cards/kpi/all`
    );
    return apiResult<CherryPayApi.CherryPayCardsKpiAll>(result);
  }

  async getDisbursementAccTransactionReportCsv(businessId: string) {
    const result = await this.cherryPayClient.get(
      `/cherrypay/v1/${businessId}/reports/cards/disbursement-account-transactions/csv`,
      {
        params: {
          timeZone: DateUtil.getBrowserTimeZone(),
        },
      }
    );

    return apiResult<any>(result);
  }

  async getDisbursementAccTransactionsReport(
    businessId: string
  ): Promise<
    ApiResult<CherryPayApi.CherryPayDisbursementAccTransactionReportResult>
  > {
    const result = await this.cherryPayClient.get(
      `/cherrypay/v1/${businessId}/reports/cards/disbursement-account-transactions`
    );
    return apiResult<CherryPayApi.CherryPayDisbursementAccTransactionReportResult>(
      result
    );
  }

  async cloneCherryPassNotificationSchedule(
    businessId: string,
    cherryPassNotificationScheduleId: string
  ): Promise<ApiResult<CherryPayApi.CherryPassNotificationSchedule>> {
    const result = await this.cherryPayClient.post(
      `/cherrypay/v1/${businessId}/pass-notifications/notification-schedules/${cherryPassNotificationScheduleId}/clone`
    );
    return apiResult<CherryPayApi.CherryPassNotificationSchedule>(result);
  }

  async getCurrentUser(): Promise<ApiResult<any>> {
    const result = await this.cherryPayClient.get("/cherrypay/v1/me");
    return apiResult(result);
  }

  /**
   * Returns a list of venues the current user has permission to manage.
   * @returns
   */
  async getBusinesses(
    businessId?: string
  ): Promise<ApiResult<CherryPayApi.BusinessSummary[]>> {
    const result = await this.cherryPayClient.get(
      "/cherrypay/v1/business/summary",
      {
        params: businessId ? { businessId } : {},
      }
    );

    return apiResult<CherryPayApi.BusinessSummary[]>(result);
  }

  /**
   * Returns an individual business summary
   * @returns
   */
  async getBusiness(
    businessId: string
  ): Promise<ApiResult<CherryPayApi.BusinessSummary | null>> {
    const result = await this.cherryPayClient.get(
      "/cherrypay/v1/business/summary",
      {
        params: { businessId },
      }
    );

    const findBusinessInResults = (data: any) =>
      data.find(
        (business: CherryPayApi.BusinessSummary) =>
          business.BusinessId === businessId
      ) ?? null;

    return apiResult<CherryPayApi.BusinessSummary | null>(
      result,
      findBusinessInResults
    );
  }

  async searchMembersQuery(
    businessId: string,
    query?: string,
    membershipNumber?: string,
    firstName?: string,
    lastName?: string,
    dateOfBirth?: string,
    filter?: string,
    continuationToken?: string,
    limit?: number,
    extendedFieldFilters?: { [key: string]: string },
    signal?: AbortSignal
  ): Promise<ApiResult<CherryPayApi.MemberQueryResultSetResponse>> {
    const params: any = {
      q: query,
      limit: limit ?? 30,
      continuationToken,
      membershipNumber,
      firstName,
      lastName,
    };

    if (dateOfBirth) {
      params.birthDateEqualTo = dateOfBirth;
    }

    if (extendedFieldFilters) {
      Object.entries(extendedFieldFilters).forEach(([key, value]) => {
        if (value) {
          params[key] = value;
        }
      });
    }

    const result = await this.cherryPayClient.get(
      `/cherrypay/v1/${businessId}/members${filter ? filter : ""}`,
      {
        params,
        signal,
      }
    );

    return apiResult(result);
  }

  async searchEventsQuery(
    businessId: string,
    query?: string,
    continuationToken?: string,
    limit?: number,
    startDateFrom?: string,
    startDateTo?: string,
    endDateFrom?: string,
    endDateTo?: string,
    status?: CherryPayApi.EventStatus,
    sortBy?: CherryPayApi.EventSortByOption
  ): Promise<ApiResult<CherryPayApi.EventQueryResultSetResponse>> {
    const result = await this.cherryPayClient.get(
      `/cherrypass/v1/${businessId}/events`,
      {
        params: {
          q: query,
          limit: limit ?? 30,
          continuationToken,
          startDateFrom,
          startDateTo,
          endDateFrom,
          endDateTo,
          status,
          sortBy,
        },
      }
    );

    return apiResult(result);
  }

  async getEvent(
    businessId: string,
    eventId: string
  ): Promise<ApiResult<CherryPayApi.Event>> {
    const result = await this.cherryPayClient.get(
      `/cherrypass/v1/${businessId}/events/${eventId}`
    );

    return apiResult(result, (data) => data);
  }

  async getEventAttendance(
    businessId: string,
    eventId: string,
    continuationToken?: string
  ): Promise<ApiResult<CherryPayApi.AttendanceScanResultSetResponse>> {
    const result = await this.cherryPayClient.get(
      `/cherrypass/v1/${businessId}/events/${eventId}/attendance`,
      {
        params: {
          continuationToken,
        },
      }
    );

    return apiResult(result, (data) => data);
  }

  async getEventKpis(
    businessId: string,
    eventId: string
  ): Promise<ApiResult<CherryPayApi.EventAttendanceScanKpis>> {
    const result = await this.cherryPayClient.get(
      `/cherrypass/v1/${businessId}/events/${eventId}/attendance/kpi`
    );

    return apiResult(result, (data) => data);
  }

  async searchPushNotificationsQuery(
    businessId: string,
    query?: string,
    continuationToken?: string,
    limit?: number,
    status?: CherryPayApi.NotificationScheduleStatus,
    sortBy?: CherryPayApi.NotificationScheduleSortByOption
  ): Promise<
    ApiResult<CherryPayApi.NotificationScheduleQueryResultSetResponse>
  > {
    const result = await this.cherryPayClient.get(
      `/cherrypay/v1/${businessId}/pass-notifications/notification-schedules`,
      {
        params: {
          q: query,
          limit: limit ?? 30,
          continuationToken,
          status,
          sortBy,
        },
      }
    );

    return apiResult(result);
  }

  async deleteCherryPassNotificationSchedule(
    businessId: string,
    cherryPassNotificationScheduleId: string
  ): Promise<ApiResult<void>> {
    const result = await this.cherryPayClient.delete(
      `/cherrypay/v1/${businessId}/pass-notifications/notification-schedules/${cherryPassNotificationScheduleId}`
    );

    return apiResult(result);
  }

  async searchMembersPage(
    url: string
  ): Promise<ApiResult<CherryPayApi.MemberQueryResultSetResponse>> {
    const result = await this.cherryPayClient.get(url);

    return apiResult<CherryPayApi.MemberQueryResultSetResponse>(result);
  }

  async getMember(
    businessId: string,
    memberId: string,
    signal?: AbortSignal
  ): Promise<ApiResult<CherryPayApi.Member>> {
    const result = await this.cherryPayClient.get(
      `/cherrypay/v1/${businessId}/members/${memberId}`,
      { signal }
    );

    return apiResult(result, (data) => data.Results[0]);
  }

  async searchUsers(
    businessId: string,
    query: string,
    continuationToken?: string
  ): Promise<ApiResult<CherryPayApi.UserAccountQueryResultSetResponse>> {
    const queryParam = query.length > 0 ? query : undefined;

    const result = await this.cherryPayClient.get(
      `/cherrypay/v1/${businessId}/user`,
      {
        params: { q: queryParam, limit: 30, continuationToken },
      }
    );

    return apiResult(result);
  }

  async searchUsersPage(
    url: string
  ): Promise<ApiResult<CherryPayApi.UserAccountQueryResultSetResponse>> {
    const result = await this.cherryPayClient.get(url);

    return apiResult(result);
  }

  async getUser(
    businessId: string,
    userUid: string
  ): Promise<ApiResult<CherryPayApi.UserAccount>> {
    const result =
      await this.cherryPayClient.get<CherryPayApi.UserAccountQueryResultSetResponse>(
        `/cherrypay/v1/${businessId}/user`,
        {
          params: { limit: 100 },
        }
      );

    const findUserInResult = (data: any) =>
      data.Results.find(
        (user: CherryPayApi.UserAccount) => user.Uid === userUid
      );

    return apiResult(result, findUserInResult);
  }

  async createUser(
    user: { Username: string; DisplayName: string; Password?: string },
    businessId: string
  ): Promise<ApiResult<CherryPayApi.UserAccount>> {
    const result = await this.cherryPayClient.post(
      `/cherrypay/v1/${businessId}/user`,
      JSON.stringify(user),
      {
        headers: {
          "content-type": "application/json",
        },
      }
    );

    return apiResult(result);
  }

  async updateUserRoles(
    businessId: string,
    userUid: string,
    roleIds: string[],
    product: string = "CherryPay"
  ): Promise<ApiResult<CherryPayApi.UserAccount>> {
    const result = await this.cherryPayClient.put(
      `/iam/v1/${businessId}/users/${userUid}/roles`,
      {
        Product: product,
        RoleIds: roleIds,
      }
    );

    return apiResult(result);
  }

  async updateUserAuthorisedVenues(
    businessId: string,
    userUid: string,
    authorisedVenues: string[]
  ): Promise<ApiResult<CherryPayApi.UserAccount>> {
    const result = await this.cherryPayClient.put(
      `/cherrypay/v1/${businessId}/user/${userUid}/authorised-venue`,
      authorisedVenues
    );

    return apiResult(result);
  }

  async deleteUser(
    businessId: string,
    userUid: string,
    userEmail: string
  ): Promise<ApiResult<CherryPayApi.UserAccount>> {
    const result = await this.cherryPayClient.delete(
      `/cherrypay/v1/${businessId}/user/${userUid}`,
      { params: { email: userEmail } }
    );

    return apiResult(result);
  }

  async lookupAbnRecord(
    method: string,
    query: string
  ): Promise<ApiResult<CherryPayApi.ABNRecord[]>> {
    if (["abn", "acn", "name"].indexOf(method) === -1) {
      throw new Error(`Invalid ABN lookup type '${method}'`);
    }

    const result = await this.cherryPayClient.get(
      `/cherrypay/v1/business/lookup/${method}`,
      { params: { q: query } }
    );

    return apiResult(result);
  }

  async createBusiness(
    abnRecord: CherryPayApi.ABNRecord
  ): Promise<ApiResult<any>> {
    const result = await this.cherryPayClient.post(
      "/cherrypay/v1/business",
      abnRecord
    );

    return apiResult(result);
  }

  async getPointsRequestModalObjects(
    businessId: string,
    memberId: string
  ): Promise<
    ApiResult<{
      Member: CherryPayApi.Member;
      PointsTypes: CherryPayApi.PointsType[];
    }>
  > {
    const result = await this.cherryPayClient.get(
      `/cherrypay/v1/${businessId}/members/${memberId}/points-request-modal`
    );

    return apiResult(result);
  }

  async getPointsToCardTransferModalObjects(
    businessId: string,
    memberId: string
  ): Promise<
    ApiResult<{
      Member: CherryPayApi.Member;
      PointsTypes: CherryPayApi.PointsType[];
      CherryPayCards: CherryPayApi.CherryPayCardListItem[];
      Minimum: number | null;
      Maximum: number | null;
    }>
  > {
    const result = await this.cherryPayClient.get(
      `/cherrypay/v1/${businessId}/members/${memberId}/points-to-card-transfer-request-modal`
    );

    return apiResult(result);
  }

  async getCPCCardInviteModalObjects(
    businessId: string,
    memberId: string,
    cardProgramType: "instantgift" | "reloadable"
  ): Promise<
    ApiResult<{
      Member: CherryPayApi.Member;
      Minimum?: number | null;
      Maximum?: number | null;
      IsSMSOptional?: boolean;
    }>
  > {
    const result = await this.cherryPayClient.get(
      `/cherrypay/v1/${businessId}/members/${memberId}/issue-member-cherrypay-card-request-modal/${cardProgramType}`
    );

    return apiResult(result);
  }

  async getPointsBalance(
    businessId: string,
    memberId: string,
    pointsTypeId: string
  ): Promise<ApiResult<CherryPayApi.PointsBalance>> {
    const result = await this.cherryPayClient.get(
      `/cherrypay/v1/${businessId}/members/${memberId}/points/${pointsTypeId}`
    );

    return apiResult(result);
  }

  async getMemberCardList(
    businessId: string,
    memberId: string
  ): Promise<ApiResult<CherryPayApi.CherryPayCardCompact[]>> {
    const result = await this.cherryPayClient.get(
      `/cherrypay/v1/${businessId}/members/${memberId}/cards`
    );

    return apiResult(result);
  }

  async transferCardFunds(
    businessId: string,
    memberId: string,
    sourceCardId: string,
    targetCardId: string,
    amount: number,
    description: string
  ): Promise<ApiResult<string>> {
    const result = await this.cherryPayClient.post(
      `/cherrypay/v1/${businessId}/cards/${sourceCardId}/transfer-funds/${targetCardId}`,
      {
        Value: amount,
        Description: description,
      },
      {
        params: {
          memberId: memberId,
        },
      }
    );

    return apiResult<string>(result);
  }

  async getDigitalMembershipCardList(
    businessId: string,
    memberId: string,
    continuationToken?: string
  ): Promise<
    ApiResult<CherryPayApi.CherryPassSummaryItemQueryResultSetResponse>
  > {
    const result = await this.cherryPayClient.get(
      `/cherrypay/v1/${businessId}/members/${memberId}/passes`,
      {
        params: { continuationToken },
      }
    );

    return apiResult(result);
  }

  async getDefaultPointsBalance(
    businessId: string,
    memberId: string
  ): Promise<ApiResult<CherryPayApi.PointsBalance>> {
    const result = await this.cherryPayClient.get(
      `/cherrypay/v1/${businessId}/members/${memberId}/points`
    );

    return apiResult(result);
  }

  async awardPoints(
    businessId: string,
    memberId: string,
    pointsTypeId: string,
    payload: {
      PointsByDollarValue: number;
      PointsByQuantity: number;
      Note: string;
      CherryPayUserName: string;
    }
  ): Promise<ApiResult<any>> {
    const result = await this.cherryPayClient.post(
      `cherrypay/v1/${businessId}/members/${memberId}/points/${pointsTypeId}/award`,
      { ...payload }
    );

    return apiResult(result);
  }

  async getOverridableProperties(businessId: string): Promise<ApiResult<any>> {
    const result = await this.cherryPayClient.get(
      `/cherrypay/v1/${businessId}/members/overridable-properties`
    );

    return apiResult(result);
  }

  async setMemberProperties(
    businessId: string,
    memberId: string,
    properties: CherryPayApi.OverriablePropertyPayload<CherryPayApi.MemberOverridableProperties>
  ): Promise<ApiResult<CherryPayApi.Member>> {
    const result = await this.cherryPayClient.patch(
      `/cherrypay/v1/${businessId}/members/${memberId}/override-property`,
      properties
    );

    return apiResult(result);
  }

  async sendNonMemberCherryPayCardInvitation(
    businessId: string,
    firstName: string,
    lastName: string,
    mobileNumber: string,
    emailAddress: string,
    initialCardValue: number,
    cpcCardType: string,
    offerExpiryDate?: string,
    plasticExpiryDate?: string,
    customInvitationSMSMessageBodyFormat?: string | null
  ): Promise<ApiResult<{}>> {
    // Add EOD on offerExpiryDate and plasticExpiryDate
    plasticExpiryDate = plasticExpiryDate + "T23:59:59.9999999Z";
    offerExpiryDate = offerExpiryDate + "T23:59:59.9999999Z";
    const result = await this.cherryPayClient.post(
      `/cherrypay/v1/${businessId}/non-members/issue/cpc/${cpcCardType}`,
      {
        FirstName: firstName,
        LastName: lastName,
        MobileNumber: mobileNumber,
        EmailAddress: emailAddress,
        InitialCardValue: initialCardValue,
        OfferExpiryDate: offerExpiryDate,
        PlasticExpiryDate: plasticExpiryDate,
        CustomInvitationSMSMessageBodyFormat:
          customInvitationSMSMessageBodyFormat,
      }
    );

    return apiResult(result);
  }

  async sendDigitalMembershipInvitation(
    businessId: string,
    memberId: string,
    request: CherryPayApi.DMCInvitationRequest
  ): Promise<ApiResult<{}>> {
    const result = await this.cherryPayClient.post(
      `/cherrypay/v1/${businessId}/members/${memberId}/invite/dmc`,
      request
    );

    return apiResult(result);
  }

  async sendCherryPayCardInvitation(
    businessId: string,
    memberId: string,
    contactDetailType: CherryPayApi.ContactDetailsType,
    initialCardValue: number,
    sendAppDownloadSms: boolean | null,
    cardProgramType: "instantgift" | "reloadable"
  ): Promise<ApiResult<{}>> {
    const result = await this.cherryPayClient.post(
      `/cherrypay/v1/${businessId}/members/${memberId}/issue/cpc/${cardProgramType}`,
      {
        MemberContactDetailsType: contactDetailType,
        InitialCardValue: initialCardValue,
        SendAppDownloadSMS: sendAppDownloadSms,
      }
    );

    return apiResult(result);
  }

  async transferPointsToCard(
    businessId: string,
    memberId: string,
    cardId: string,
    pointsTypeId: string,
    payload: {
      PointsByDollarValue: number;
      PointsByQuantity: number;
      Note: string;
      Description: string;
      CherryPayUserName: string;
    }
  ): Promise<ApiResult<any>> {
    const result = await this.cherryPayClient.post(
      `cherrypay/v1/${businessId}/members/${memberId}/cards/${cardId}/funds/transfer-points/${pointsTypeId}`,
      { ...payload }
    );

    return apiResult(result);
  }

  async getPassConfig(
    businessId: string,
    passConfigurationId: string
  ): Promise<ApiResult<any>> {
    const url = `cherryplay/v1/${businessId}/pass-configurations/${passConfigurationId}`;
    const result = await this.cherryPayClient.get(url);

    return apiResult(result);
  }

  async updatePassConfig(
    businessId: string,
    passConfigurationId: string,
    updatedDoc: any
  ): Promise<ApiResult<any>> {
    const url = `cherryplay/v1/${businessId}/pass-configurations/${passConfigurationId}`;
    if (!updatedDoc.isUnprocessed) {
      throw new Error("Pass Document needs to be unprocessed.");
    }

    delete updatedDoc.isUnprocessed;

    // make sure all auxillary fields are saved as secondary
    (updatedDoc?.iOSAdditionalFields || []).map((f: any) => {
      if (f.FieldLocation === "Auxiliary") {
        f.FieldLocation = "Secondary";
      }
    });

    const result = await this.cherryPayClient.put(url, updatedDoc);

    return apiResult(result);
  }

  async getPassImages(
    businessId: string,
    passConfigurationId: string
  ): Promise<any[]> {
    const urlTemplate = `cherryplay/v1/${businessId}/pass-configurations/${passConfigurationId}/images/{passImageType}`;
    const passImageTypes = [
      "GoogleProgramLogo",
      "GoogleHeroImage",
      "AppleIcon",
      "AppleIcon2X",
      "AppleLogo",
      "AppleLogo2X",
      "AppleStrip",
      "AppleStrip2X",
    ];

    const results = await Promise.all(
      passImageTypes.map(async (imageType) => {
        const imageData = await this.cherryPayClient.get(
          urlTemplate.replace("{passImageType}", imageType),
          {
            responseType: "arraybuffer",
          }
        );
        return {
          imageData,
          imageType,
        };
      })
    );

    return results;
  }

  async savePassImage(
    businessId: string,
    passConfigurationId: string,
    passImageType: string,
    file: File
  ): Promise<any[]> {
    const url = `cherryplay/v1/${businessId}/pass-configurations/${passConfigurationId}/images/${passImageType}`;
    const data = new FormData();
    data.set("file", file);
    return await this.cherryPayClient.post(url, data);
  }
  async removePassImage(
    businessId: string,
    passConfigurationId: string,
    passImageType: string
  ): Promise<any[]> {
    const url = `cherryplay/v1/${businessId}/pass-configurations/${passConfigurationId}/images/${passImageType}`;
    return await this.cherryPayClient.delete(url);
  }

  async getPassTokens(
    businessId: string,
    passConfigurationId: string
  ): Promise<any> {
    const url = `cherryplay/v1/${businessId}/pass-configurations/metadata/supported-token-fields?passConfigurationId=${passConfigurationId}`;
    const result = await this.cherryPayClient.get(url);
    console.log("result", result);
    const allTokens = {
      ...(result.data.Member || {}),
      ...(result.data.Messaging || {}),
      ...(result.data.Business || {}),
      ...(result.data.Venue || {}),
    };
    return allTokens;
  }

  async getMembershipRequests(
    businessId: string,
    options: {
      includeDeleted?: boolean;
      q?: string;
      limit?: number;
      startDate?: string;
      endDate?: string;
      continuationToken?: string;
      isFinalised?: boolean;
      registrationFormConfigurationId?: string;
      status?: CherryPayApi.MembershipRequestStatus[];
    }
  ): Promise<ApiResult<CherryPayApi.MembershipRequestResultSet>> {
    const { status, ...rest } = options;
    const params = new URLSearchParams();

    Object.entries(rest).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        params.append(key, String(value));
      }
    });

    status?.forEach((membershipRequestStatus) => {
      params.append("status", membershipRequestStatus);
    });

    const result = await this.cherryPayClient.get(
      `cherrypay/v1/${businessId}/odma/membership-requests`,
      {
        params,
      }
    );
    return apiResult<CherryPayApi.MembershipRequestResultSet>(result);
  }

  async getMembershipRequest(
    businessId: string,
    sessionId: string
  ): Promise<ApiResult<CherryPayApi.MembershipRequestDocument>> {
    const result = await this.cherryPayClient.get(
      `cherrypay/v1/${businessId}/odma/membership-requests/${sessionId}`
    );
    return apiResult(result);
  }

  async getEnumConfigurations(
    businessId: string,
    enumType: string
  ): Promise<ApiResult<CherryPayApi.EnumConfigurationItem[]>> {
    const result = await this.cherryPayClient.get(
      `cherrypay/v1/${businessId}/configuration/enums/${enumType}`
    );

    return apiResult(result);
  }

  async getMemberDataOptions(
    businessId: string,
    fieldName: string,
    venueId?: string
  ): Promise<ApiResult<CherryPayApi.ScalarValue[]>> {
    const result = await this.cherryPayClient.get(
      `cherrypay/v1/${businessId}/configuration/member-data/${fieldName}`,
      {
        params: {
          venueId: venueId,
        },
      }
    );
    return apiResult(result);
  }

  async getRegistrationFormConfigurationSummaryItems(
    businessId: string
  ): Promise<
    ApiResult<CherryPayApi.RegistrationFormConfigurationSummaryItem[]>
  > {
    const result = await this.cherryPayClient.get(
      `/configuration/${businessId}/registration-form-configuration`
    );
    return apiResult(result);
  }

  async getMembershipRenewalFormConfigurationSummaryItems(
    businessId: string
  ): Promise<
    ApiResult<CherryPayApi.MembershipRenewalFormConfigurationSummaryItem[]>
  > {
    const result = await this.cherryPayClient.get(
      `/configuration/${businessId}/membership-renewal-form-configuration`
    );
    return apiResult(result);
  }

  async getMembershipApplicationReports(
    businessId: string,
    params: {
      limit: number;
      registrationFormConfigurationId?: string;
      startDate?: string;
      endDate?: string;
      continuationToken?: string;
      isFinalised?: boolean;
      status?: CherryPayApi.MembershipRequestStatus[];
    }
  ): Promise<
    ApiResult<CherryPayApi.MembershipApplicationSummaryQueryResultSet>
  > {
    const { status, ...rest } = params;
    const searchParams = new URLSearchParams();

    Object.entries(rest).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        searchParams.append(key, String(value));
      }
    });

    status?.forEach((membershipRequestStatus) => {
      searchParams.append("statuses", membershipRequestStatus);
    });

    const result = await this.cherryPayClient.get(
      `/cherrypay/v1/${businessId}/reports/cards/membership-application`,
      {
        params: searchParams,
      }
    );
    return apiResult<CherryPayApi.MembershipApplicationSummaryQueryResultSet>(
      result
    );
  }

  async getMembershipApplicationReportCsv(
    businessId: string,
    params: {
      registrationFormConfigurationId?: string;
      startDate?: string;
      endDate?: string;
      isFinalised?: boolean;
      status?: CherryPayApi.MembershipRequestStatus[];
    }
  ) {
    const { status, ...rest } = params;
    const searchParams = new URLSearchParams();

    Object.entries(rest).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        searchParams.append(key, String(value));
      }
    });

    status?.forEach((membershipRequestStatus) => {
      searchParams.append("statuses", membershipRequestStatus);
    });

    searchParams.append("timeZone", DateUtil.getBrowserTimeZone());

    const result = await this.cherryPayClient.get(
      `/cherrypay/v1/${businessId}/reports/cards/membership-application/csv`,
      {
        params: searchParams,
      }
    );

    return apiResult<any>(result);
  }

  async getPassesByMembers(
    businessId: string,
    params: {
      limit: number;
      continuationToken?: string;
    }
  ): Promise<ApiResult<CherryPayApi.CherryPassMemberReportItemQueryResultSet>> {
    const result = await this.cherryPayClient.get(
      `/cherrypay/v1/${businessId}/reports/cards/passes-by-member`,
      {
        params: {
          ...params,
        },
      }
    );
    return apiResult<CherryPayApi.CherryPassMemberReportItemQueryResultSet>(
      result
    );
  }

  async getPassesByMembersCsv(businessId: string) {
    const result = await this.cherryPayClient.get(
      `/cherrypay/v1/${businessId}/reports/cards/passes-by-member/csv`,
      {
        params: {
          timeZone: DateUtil.getBrowserTimeZone(),
        },
      }
    );

    return apiResult<any>(result);
  }

  async getMembershipRenewalRequests(
    businessId: string,
    options: {
      membershipRenewalFormConfigurationId?: string;
      q?: string;
      limit?: number;
      continuationToken?: string;
      includeDeleted?: boolean;
      startDate?: string;
      endDate?: string;
    }
  ): Promise<
    ApiResult<CherryPayApi.MembershipRenewalRequestSummaryItemQueryResultSet>
  > {
    const result = await this.cherryPayClient.get(
      `cherrypay/v1/${businessId}/odma/membership-renewal-requests`,
      {
        params: options,
      }
    );
    return apiResult<CherryPayApi.MembershipRenewalRequestSummaryItemQueryResultSet>(
      result
    );
  }

  async getMembershipRenewalApplicationReports(
    businessId: string,
    params: {
      limit: number;
      membershipRenewalFormConfigurationId?: string;
      startDate?: string;
      endDate?: string;
      continuationToken?: string;
    }
  ): Promise<
    ApiResult<CherryPayApi.MembershipRenewalRequestReportItemQueryResultSet>
  > {
    const result = await this.cherryPayClient.get(
      `/cherrypay/v1/${businessId}/reports/cards/membership-renewal-request`,
      {
        params: {
          ...params,
        },
      }
    );
    return apiResult<CherryPayApi.MembershipRenewalRequestReportItemQueryResultSet>(
      result
    );
  }

  async getMembershipRenewalApplicationReportCsv(
    businessId: string,
    membershipRenewalFormConfigurationId?: string,
    startDate?: string,
    endDate?: string
  ) {
    const result = await this.cherryPayClient.get(
      `/cherrypay/v1/${businessId}/reports/cards/membership-renewal-request/csv`,
      {
        params: {
          startDate,
          endDate,
          membershipRenewalFormConfigurationId,
          timeZone: DateUtil.getBrowserTimeZone(),
        },
      }
    );

    return apiResult<any>(result);
  }

  async createHubMembershipMember(
    memberUpdate: Partial<CherryPayApi.MemberUpdate>,
    businessId: string,
    params: {
      venueId?: string | null;
    }
  ): Promise<ApiResult<CherryPayApi.Member>> {
    const result = await this.cherryPayClient.post(
      `/cherrypay/v1/${businessId}/hub-membership/members`,
      memberUpdate,
      {
        params: {
          ...params,
        },
      }
    );

    return apiResult(result);
  }

  async updateHubMembershipMember(
    memberUpdate: Partial<CherryPayApi.MemberUpdate>,
    businessId: string,
    memberId: string
  ): Promise<ApiResult<CherryPayApi.Member>> {
    const result = await this.cherryPayClient.put(
      `/cherrypay/v1/${businessId}/hub-membership/members/${memberId}`,
      memberUpdate
    );

    return apiResult(result);
  }

  async deleteHubMembershipMember(
    businessId: string,
    memberId: string
  ): Promise<ApiResult<any>> {
    const result = await this.cherryPayClient.delete(
      `/cherrypay/v1/${businessId}/hub-membership/members/${memberId}`
    );

    return apiResult(result);
  }

  async getExtendedFieldDefinitions(
    businessId: string
  ): Promise<ApiResult<CherryPayApi.ExtendedFieldDefinition[]>> {
    const result = await this.cherryPayClient.get(
      `/cherrypay/v1/${businessId}/hub-membership/extended-fields`
    );

    return apiResult(result);
  }

  async convertHubMembershipGeocodedToAddress(
    businessId: string,
    geocodedAddress?: CherryPayApi.GeocodedAddress
  ): Promise<ApiResult<CherryPayApi.Address>> {
    const result = await this.cherryPayClient.post(
      `/cherrypay/v1/${businessId}/hub-membership/members/geocoded-addresses/transform`,
      geocodedAddress
    );

    return apiResult(result);
  }

  async getHubMembershipTypes(
    businessId: string,
    params: {
      venueId?: string | null;
      memberId?: string | null;
    }
  ): Promise<ApiResult<CherryPayApi.KeyValuePairString[]>> {
    const result = await this.cherryPayClient.get(
      `/cherrypay/v1/${businessId}/hub-membership/membership-types`,
      {
        params: {
          ...params,
        },
      }
    );

    return apiResult<CherryPayApi.KeyValuePairString[]>(result);
  }

  async getHubMembershipLevels(
    businessId: string,
    params: {
      venueId?: string | null;
      memberId?: string | null;
    }
  ): Promise<ApiResult<CherryPayApi.KeyValuePairString[]>> {
    const result = await this.cherryPayClient.get(
      `/cherrypay/v1/${businessId}/hub-membership/membership-levels`,
      {
        params: {
          ...params,
        },
      }
    );

    return apiResult<CherryPayApi.KeyValuePairString[]>(result);
  }

  async getHubMembershipGenders(
    businessId: string,
    params: {
      venueId?: string | null;
      memberId?: string | null;
    }
  ): Promise<ApiResult<CherryPayApi.KeyValuePairString[]>> {
    const result = await this.cherryPayClient.get(
      `/cherrypay/v1/${businessId}/hub-membership/genders`,
      {
        params: {
          ...params,
        },
      }
    );

    return apiResult<CherryPayApi.KeyValuePairString[]>(result);
  }

  async getHubMembershipTitles(
    businessId: string,
    params: {
      venueId?: string | null;
      memberId?: string | null;
    }
  ): Promise<ApiResult<CherryPayApi.KeyValuePairString[]>> {
    const result = await this.cherryPayClient.get(
      `/cherrypay/v1/${businessId}/hub-membership/titles`,
      {
        params: {
          ...params,
        },
      }
    );

    return apiResult<CherryPayApi.KeyValuePairString[]>(result);
  }

  async getHubMembershipVenues(
    businessId: string
  ): Promise<ApiResult<CherryPayApi.KeyValuePairString[]>> {
    const result = await this.cherryPayClient.get(
      `/cherrypay/v1/${businessId}/hub-membership/venues`
    );

    return apiResult<CherryPayApi.KeyValuePairString[]>(result);
  }

  async getHubMembershipVenueById(
    businessId: string,
    venueId: string
  ): Promise<ApiResult<CherryPayApi.Venue>> {
    const result = await this.cherryPayClient.get(
      `/cherrypay/v1/${businessId}/hub-membership/venue/${venueId}`
    );

    return apiResult<CherryPayApi.Venue>(result);
  }

  async createEvent(
    eventUpdate: Partial<CherryPayApi.EventUpdate>,
    businessId: string
  ): Promise<ApiResult<CherryPayApi.Event>> {
    const result = await this.cherryPayClient.post(
      `/cherrypass/v1/${businessId}/events/`,
      eventUpdate
    );

    return apiResult(result);
  }

  async updateEvent(
    eventUpdate: Partial<CherryPayApi.EventUpdate>,
    businessId: string,
    eventId: string
  ): Promise<ApiResult<CherryPayApi.Event>> {
    const result = await this.cherryPayClient.put(
      `/cherrypass/v1/${businessId}/events/${eventId}`,
      eventUpdate
    );

    return apiResult(result);
  }

  async setEventStatus(
    businessId: string,
    eventId: string,
    status: string
  ): Promise<ApiResult<CherryPayApi.Event>> {
    const result = await this.cherryPayClient.put(
      `/cherrypass/v1/${businessId}/events/${eventId}/set/status/${status}`
    );

    return apiResult(result);
  }

  /**
   * Downloads event attendance data as CSV
   * @param businessId The business ID
   * @param eventConfigurationId The event configuration ID
   * @param includeInvalidScans Whether to include invalid scans in the export
   * @returns ApiResult with CSV data
   */
  async downloadEventAttendanceAsCsv(
    businessId: string,
    eventConfigurationId: string,
    includeInvalidScans: boolean
  ): Promise<ApiResult<any>> {
    const endpoint = `/cherrypass/v1/${businessId}/events/${eventConfigurationId}/attendance/csv?includeInvalidScans=${includeInvalidScans}`;
    const result = await this.cherryPayClient.get(endpoint);

    return apiResult<any>(result);
  }

  async getEligibleMemberCount(
    businessId: string,
    memberFilterQuery?: string
  ): Promise<ApiResult<CherryPayApi.MemberCountResult>> {
    const result = await this.cherryPayClient.get(
      `/cherrypass/v1/${businessId}/events/eligible-member-count`,
      {
        params: {
          memberFilterQuery,
        },
      }
    );

    return apiResult<CherryPayApi.MemberCountResult>(result);
  }

  async getNotificationScheduleEligibleMemberCount(
    businessId: string,
    memberFilterQuery?: string | null
  ): Promise<ApiResult<CherryPayApi.MemberCountResult>> {
    const result = await this.cherryPayClient.get(
      `/cherrypay/v1/${businessId}/pass-notifications/notification-schedules/eligible-member-count`,
      {
        params: {
          memberFilterQuery: memberFilterQuery ?? "",
        },
      }
    );

    return apiResult<CherryPayApi.MemberCountResult>(result);
  }

  async getNotificationScheduleEligibleMembers(
    businessId: string,
    memberFilterQuery?: string | null,
    limit?: number,
    continuationToken?: string
  ): Promise<ApiResult<CherryPayApi.MemberQueryResultSetResponse>> {
    const queryParams = new URLSearchParams();

    if (limit) queryParams.set("limit", limit.toString());
    if (continuationToken)
      queryParams.set("continuationToken", continuationToken);
    if (memberFilterQuery) {
      // Add member query filter as part of the request query itself
      const filterParams = new URLSearchParams(memberFilterQuery);
      filterParams.forEach((value, key) => {
        queryParams.append(key, value);
      });
    }

    const result = await this.cherryPayClient.get(
      `/cherrypay/v1/${businessId}/pass-notifications/notification-schedules/eligible-members?${queryParams.toString()}`
    );

    return apiResult<CherryPayApi.MemberQueryResultSetResponse>(result);
  }

  async downloadNotificationScheduleEligibleMembersAsCsv(
    businessId: string,
    memberFilterQuery?: string | null
  ): Promise<ApiResult<any>> {
    const queryParams = new URLSearchParams();

    if (memberFilterQuery) {
      // Add member query filter as part of the request query itself
      const filterParams = new URLSearchParams(memberFilterQuery);
      filterParams.forEach((value, key) => {
        queryParams.append(key, value);
      });
    }

    queryParams.set("timeZone", DateUtil.getBrowserTimeZone());

    // Unique query so the browser does not reuse a previously cached CSV.
    queryParams.set("_", Date.now().toString());

    const result = await this.cherryPayClient.get(
      `/cherrypay/v1/${businessId}/pass-notifications/notification-schedules/eligible-members/csv?${queryParams.toString()}`,
      {
        headers: {
          "Cache-Control": "no-cache",
          Pragma: "no-cache",
        },
      }
    );

    return apiResult<any>(result);
  }

  async createNotification(
    notificationUpdate: CherryPayApi.CherryPassNotificationScheduleUpdate,
    businessId: string
  ): Promise<ApiResult<CherryPayApi.CherryPassNotificationSchedule>> {
    const result = await this.cherryPayClient.post(
      `/cherrypay/v1/${businessId}/pass-notifications/notification-schedules`,
      notificationUpdate
    );

    return apiResult(result);
  }

  async updateNotification(
    notificationUpdate: CherryPayApi.CherryPassNotificationScheduleUpdate,
    businessId: string,
    cherryPassNotificationScheduleId: string
  ): Promise<ApiResult<CherryPayApi.CherryPassNotificationSchedule>> {
    const result = await this.cherryPayClient.put(
      `/cherrypay/v1/${businessId}/pass-notifications/notification-schedules/${cherryPassNotificationScheduleId}`,
      notificationUpdate
    );

    return apiResult(result);
  }

  async scheduleNotification(
    businessId: string,
    cherryPassNotificationScheduleId: string
  ): Promise<ApiResult<CherryPayApi.CherryPassNotificationSchedule>> {
    const result = await this.cherryPayClient.put(
      `/cherrypay/v1/${businessId}/pass-notifications/notification-schedules/${cherryPassNotificationScheduleId}/set/status/schedule`
    );

    return apiResult(result);
  }

  async draftScheduledCherryPassNotificationSchedule(
    businessId: string,
    cherryPassNotificationScheduleId: string
  ): Promise<ApiResult<CherryPayApi.CherryPassNotificationSchedule>> {
    const result = await this.cherryPayClient.put(
      `/cherrypay/v1/${businessId}/pass-notifications/notification-schedules/${cherryPassNotificationScheduleId}/set/status/draft`
    );

    return apiResult(result);
  }

  async reviewNotificationMessageWithAI(
    businessId: string,
    passUpdateMessage: string
  ): Promise<
    ApiResult<CherryPayApi.CherryPassNotificationScheduleReviewResult>
  > {
    const result = await this.cherryPayClient.post(
      `/cherrypay/v1/${businessId}/pass-notifications/notification-schedules/review-message-ai`,
      { PassUpdateMessage: passUpdateMessage }
    );

    return apiResult(result);
  }

  async updateCardEmailAddress(
    businessId: string,
    cardId: string,
    emailAddress: string
  ): Promise<ApiResult<any>> {
    const result = await this.cherryPayClient.patch(
      `/cherrypay/v1/${businessId}/cards/${cardId}/email-address`,
      { Value: emailAddress }
    );

    return apiResult(result);
  }

  async getMemberMobileNumbers(
    businessId: string,
    cardId: string
  ): Promise<ApiResult<string[]>> {
    const result = await this.cherryPayClient.get(
      `/cherrypay/v1/${businessId}/cards/${cardId}/mobile-numbers`
    );

    return apiResult(result);
  }

  async updateCardMobileNumber(
    businessId: string,
    cardId: string,
    mobileNumber: string
  ): Promise<ApiResult<any>> {
    const result = await this.cherryPayClient.patch(
      `/cherrypay/v1/${businessId}/cards/${cardId}/mobile-number`,
      { Value: mobileNumber }
    );

    return apiResult(result);
  }

  async updateCardAppVisibility(
    businessId: string,
    cardId: string,
    isVisibleInApp: boolean
  ): Promise<ApiResult<any>> {
    const result = await this.cherryPayClient.patch(
      `/cherrypay/v1/${businessId}/cards/${cardId}/set-app-visibility?isVisibleInApp=${isVisibleInApp}`
    );

    return apiResult(result);
  }

  async acceptTermsAndConditions(
    businessId: string,
    termsAndConditionsFeature: CherryPayApi.AcceptedTermsAndConditionsKey
  ): Promise<ApiResult<CherryPayApi.BusinessSummary>> {
    const result = await this.cherryPayClient.post(
      `/business/v1/${businessId}/terms-and-conditions/accept`,
      {
        TermsAndConditionsFeature: termsAndConditionsFeature,
      }
    );

    return apiResult(result);
  }

  async getIamRolesForProduct(
    businessId: string,
    product: string
  ): Promise<ApiResult<CherryPayApi.CherryHubRolesSummary>> {
    const result = await this.cherryPayClient.get(
      `/iam/v1/${businessId}/roles`,
      {
        params: { product },
      }
    );

    return apiResult<CherryPayApi.CherryHubRolesSummary>(result);
  }

  async getMyIamRolesForProduct(
    businessId: string,
    product: string
  ): Promise<ApiResult<CherryPayApi.CherryHubRolesSummary>> {
    const result = await this.cherryPayClient.get(
      `/iam/v1/${businessId}/users/me/roles`,
      {
        params: { product },
      }
    );

    return apiResult<CherryPayApi.CherryHubRolesSummary>(result);
  }

  async createIamRole(
    businessId: string,
    roleUpdate: CherryPayApi.CherryHubRoleUpdate
  ): Promise<ApiResult<CherryPayApi.CherryHubRoleSummaryItem>> {
    const result = await this.cherryPayClient.post(
      `/iam/v1/${businessId}/roles`,
      roleUpdate
    );

    return apiResult<CherryPayApi.CherryHubRoleSummaryItem>(result);
  }

  async updateIamRole(
    businessId: string,
    roleId: string,
    roleUpdate: CherryPayApi.CherryHubRoleUpdate
  ): Promise<ApiResult<CherryPayApi.CherryHubRoleSummaryItem>> {
    const result = await this.cherryPayClient.put(
      `/iam/v1/${businessId}/roles/${roleId}`,
      roleUpdate
    );

    return apiResult<CherryPayApi.CherryHubRoleSummaryItem>(result);
  }

  async deleteIamRole(
    businessId: string,
    roleId: string
  ): Promise<ApiResult<void>> {
    const result = await this.cherryPayClient.delete(
      `/iam/v1/${businessId}/roles/${roleId}`
    );

    return apiResult<void>(result);
  }
}
